import React, { MouseEventHandler } from "react";
import { ContentSpacer } from "../common/layout";
import { Skeleton } from "../../components/ui/skeleton";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
    CircleCheckIcon,
    CircleDashedIcon,
    EllipsisIcon,
    PenIcon,
    RocketIcon,
    Trash2Icon,
    TriangleAlertIcon,
} from "lucide-react";
import { useCurrentOrgUser, useOrg, useOrgUser } from "../../auth/org-provider";
import { DateTime } from "luxon";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import Spinner from "../../components/ui/spinner";
import { cn } from "../../lib/utils";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useDeleteProjectMutation } from "../../data/mutations/delete-project";
import { AvatarForUser } from "../../components/shared/org/avatar-for-user";
import { useProjectsOrgReadinessQuery } from "../../data/queries/projects-org-readiness";
import { useProjectsListQuery } from "../../data/queries/project-list";
import { ProjectMeta } from "../../data/types/api-types";
import { formatWorkstreamLetter } from "../../lib/projects/utils";
import { useListChatsQuery } from "../../data/queries/list-chats";
import { useDeleteChatMutation } from "../../data/mutations/delete-chat";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { useConfirmDialog } from "../../components/modals/confirmation-dialog-provider";
import { ChatType } from "tekkr-common/dist/model/chats/types";
import { StatusBar, StatusTooltipContent } from "../../components/shared/project/status-circle";

export function ProjectsListPage() {
    const org = useOrg();
    const query = useProjectsListQuery();
    const user = useCurrentOrgUser();

    const startedProjects = query.data?.startedProjects ?? [];
    const onTrackCount = startedProjects.filter((p) => p.status === "on_track" || p.status === null).length;
    const delayedCount = startedProjects.filter((p) => p.status === "delayed").length;
    const metrics = [
        {
            name: "Ongoing projects",
            value: startedProjects.length,
        },
        {
            name: "On track",
            value: onTrackCount,
            class: "text-confirmation",
        },
        {
            name: "Delayed",
            value: delayedCount,
            class: "text-destructive",
        },
        {
            name: "Upcoming projects",
            value: query.data ? query.data.upcomingProjects.length : 0,
        },
    ];

    return (
        <ContentSpacer>
            <div className={"mt-8 flex flex-row items-center gap-12"}>
                <div>
                    <h2>
                        Hey, {user.name.split(" ")[0]} {"👋"}
                    </h2>
                    <p>Welcome back to your projects, here's a summary of their status:</p>
                </div>
            </div>

            <ReadinessSummary />

            <CardsGrid>
                {metrics.map((metric) => (
                    <Card key={metric.name}>
                        <div className={"flex flex-col items-center justify-center gap-2 px-6 py-4 text-center"}>
                            <div className={cn("text-3xl font-semibold", metric.class)}>{metric.value}</div>
                            <div className={"text-xs font-medium text-muted-foreground"}>{metric.name}</div>
                        </div>
                    </Card>
                ))}
            </CardsGrid>

            <div
                className={
                    "mt-8 flex max-w-prose flex-row justify-between gap-2 rounded-md border border-primary/20 bg-primary/10 p-4"
                }
            >
                <div>
                    <div className={"self-center font-medium"}>New challenge to address?</div>
                    <div className={"text-sm opacity-65"}>Tekkr helps you structure a plan and get it delivered.</div>
                </div>
                <Button className={"group overflow-hidden"} asChild>
                    <Link to={"/new-project"}>
                        <RocketIcon
                            className={
                                "me-2 h-5 w-5 transition-all duration-500 group-hover:translate-x-1.5 group-hover:-rotate-12 group-hover:scale-90 group-active:-translate-y-8 group-active:-rotate-45"
                            }
                        />
                        <div className={"transition-all duration-500 group-hover:translate-x-0.5"}>Start a project</div>
                    </Link>
                </Button>
            </div>
            <DraftProjectChatsList />

            <div className={"-mb-2 mt-4 flex flex-row items-center gap-12"}>
                <div>
                    <h3>Ongoing Projects</h3>
                    <div className={"text-xs font-semibold text-muted-foreground"}>at {org.name}</div>
                </div>
            </div>

            <div className={"mt-3 grid grid-cols-3 gap-4"}>
                {!query.data && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className={"h-32"} />)}
                {query.data?.startedProjects.map((project) => (
                    <ProjectCard key={project.id} project={project} />
                ))}
                {query.data?.startedProjects.length === 0 && (
                    <div className={"rounded-md bg-accent p-3 text-center text-sm text-muted-foreground"}>
                        You don't have any ongoing projects.
                    </div>
                )}
            </div>

            {(query.data?.upcomingProjects.length ?? 0) > 0 && (
                <>
                    <div className={"-mb-4 mt-8 text-sm font-medium text-muted-foreground"}>Upcoming Projects</div>
                    <div className={"mt-3 grid grid-cols-3 gap-4"}>
                        {query.data?.upcomingProjects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                </>
            )}
        </ContentSpacer>
    );
}

function CardsGrid(props: React.PropsWithChildren) {
    return <div className={"grid grid-cols-3 gap-4 md:grid-cols-4 lg:grid-cols-5"}>{props.children}</div>;
}

function ProjectCard({ project }: { project: ProjectMeta }) {
    const creator = useOrgUser(project.creatorId);
    const currentUser = useCurrentOrgUser();
    const deleteProjectMutation = useDeleteProjectMutation();
    const confirm = useConfirmDialog();

    const deleteClickHandler: MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        confirm(
            {
                title: "Delete project",
                body: (
                    <div className={"flex flex-col gap-3 pb-3"}>
                        <div>
                            Are you sure you want to delete the project{" "}
                            <strong className={"font-semibold"}>{project.name}</strong>?
                        </div>
                        <div className={"text-sm text-muted-foreground"}>This action cannot be undone.</div>
                    </div>
                ),
                confirmButtonText: "Delete",
                confirmButtonVariant: "destructive",
            },
            () => {
                deleteProjectMutation.mutate(project.id);
            }
        )();
    };

    const userIsProjectCreator = project.creatorId === currentUser.id;

    return (
        <Link key={project.id} to={`/project/${project.id}`}>
            <div
                className={
                    "flex h-full cursor-pointer flex-col gap-2 rounded-md border bg-card p-4 transition-all animate-in slide-in-from-bottom-1 hover:scale-[99.5%] hover:bg-accent"
                }
            >
                <div className={"flex flex-row items-start justify-between gap-0.5"}>
                    <ProjectStateChip status={project.status} />
                    {userIsProjectCreator && (
                        <DropdownMenu open={deleteProjectMutation.isPending ? true : undefined}>
                            <DropdownMenuTrigger asChild>
                                <Button variant={"ghost"} size={"sm2"}>
                                    <EllipsisIcon className={"h-5 w-5 text-muted-foreground"} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={deleteClickHandler} className={"text-destructive"}>
                                    {deleteProjectMutation.isPending ? (
                                        <Spinner className={"me-1 h-4 w-4"} />
                                    ) : (
                                        <Trash2Icon className={"me-1 h-4 w-4"} />
                                    )}
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                <h4>{project.name}</h4>

                <ProjectProgressBar progress={project.progress} status={project.status} />

                {project.timeline.to && (
                    <div className={"mt-2 text-sm font-medium"}>
                        Timeline: {DateTime.fromISO(project.timeline.to).toLocaleString(DateTime.DATE_MED)}
                    </div>
                )}

                <div
                    className={
                        "mt-auto flex flex-row flex-wrap items-center justify-start gap-1 text-xs font-medium text-muted-foreground"
                    }
                >
                    <div className={"me-1"}>Project owner:</div>
                    <div className={"flex flex-row items-center gap-2"}>
                        <AvatarForUser user={creator} className={"h-6 w-6"} /> {creator.name}
                    </div>
                </div>
            </div>
        </Link>
    );
}

function DraftProjectChatsList() {
    const chats = useListChatsQuery({
        filter: { chatType: [ChatType.CreateProject], scope: ["draft"] },
    });
    const deleteChatMutation = useDeleteChatMutation({
        onSuccess: () => {
            void chats.refetch();
        },
    });

    if (chats.isPending) {
        return (
            <div
                className={
                    "text-medium flex flex-row gap-2 self-start rounded-md border border-secondary px-4 py-2 text-sm text-muted-foreground"
                }
            >
                <Spinner className={"h-5 w-5"} />
                Loading Project Drafts...
            </div>
        );
    }

    if (chats.data?.length === 0) {
        return null;
    }

    return (
        <div>
            <CardsGrid>
                {chats.data?.map((chat) => (
                    <DraftChatCard
                        key={chat.id}
                        chat={chat}
                        onDelete={async () => {
                            await deleteChatMutation.mutateAsync({ chatId: chat.id });
                        }}
                    />
                ))}
            </CardsGrid>
        </div>
    );
}

function DraftChatCard({
    chat,
    onDelete,
}: {
    chat: { id: string; title: string; createdAt: Date };
    onDelete: () => Promise<void>;
}) {
    const confirm = useConfirmDialog();

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        confirm(
            {
                title: `Delete Draft ${chat.title}?`,
                body: "Do you really want to delete this chat?",
                confirmButtonText: "Delete",
                confirmButtonVariant: "destructive",
            },
            onDelete
        )();
    };

    return (
        <Link to={`/new-project?chat_id=${chat.id}`}>
            <div
                className={
                    "flex flex-col rounded-md border border-secondary/20 bg-secondary/10 p-3 transition-all hover:scale-[99%] hover:bg-accent"
                }
            >
                <div className={"mb-1 flex flex-row items-start gap-2 text-xs font-semibold text-foreground/45"}>
                    <PenIcon className={"h-3.5 w-3.5"} />
                    <span>Project draft</span>
                    <div className={"grow"} />
                    <div
                        onClick={handleDeleteClick}
                        className={
                            "-me-1 -mt-1 cursor-pointer rounded-sm p-1 text-foreground opacity-45 transition-all hover:bg-destructive hover:text-white hover:opacity-100"
                        }
                    >
                        <Trash2Icon className={"h-4 w-4"} />
                    </div>
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className={"line-clamp-1 text-sm"}>{chat.title}</div>
                    </TooltipTrigger>
                    <TooltipContent className={"max-w-xs"}>{chat.title}</TooltipContent>
                </Tooltip>
                <div className={"text-sm font-medium text-muted-foreground"}>{formatDraftChatDate(chat.createdAt)}</div>
            </div>
        </Link>
    );
}

function formatDraftChatDate(time: Date) {
    const now = DateTime.now();
    const date = DateTime.fromJSDate(time);
    if (now.diff(date, "days").days < 1) {
        return `Today, ${date.toLocaleString(DateTime.TIME_SIMPLE)}`;
    }
    if (now.diff(date, "weeks").weeks < 1) {
        return date.toFormat("cccc");
    }
    return date.toLocaleString(DateTime.DATE_MED);
}

function ProjectStateChip({ status }: { status: ProjectMeta["status"] }) {
    switch (status) {
        case "draft":
            return <Badge variant={"secondary"}>Draft</Badge>;
        case "not_started":
            return <Badge variant={"secondary"}>Not started</Badge>;
        case "completed":
            return <Badge variant={"secondary"}>Completed</Badge>;
        case "delayed":
            return (
                <Badge variant={"secondary"} className={"border border-destructive bg-transparent text-destructive"}>
                    Delayed
                </Badge>
            );
        case "on_track":
            return (
                <Badge variant={"secondary"} className={"border border-confirmation bg-transparent text-confirmation"}>
                    On track
                </Badge>
            );
        default:
            return null;
    }
}

function ProjectProgressBar({
    progress,
    status,
}: {
    progress: ProjectMeta["progress"];
    status: ProjectMeta["status"];
}) {
    const totalTasks = progress?.statusCounts.total ?? 0;
    return (
        <div className={"flex flex-col gap-1"}>
            <div className={"flex h-1.5 w-full flex-row items-center gap-1"}>
                {progress?.workstreams.map((wsProgress, index) => {
                    if (wsProgress.statusCounts.total === 0) {
                        return null;
                    }
                    return (
                        <Tooltip key={wsProgress.workstreamId}>
                            <TooltipTrigger asChild>
                                <div
                                    style={{
                                        flexBasis: `${(wsProgress.statusCounts.total / totalTasks) * 100}%`,
                                    }}
                                    className="h-full"
                                >
                                    <StatusBar counts={wsProgress.statusCounts} className="h-full" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side={"bottom"} className={"max-w-[280px] p-0"}>
                                <div className={"line-clamp-2 px-3 pt-2.5 text-xs font-semibold"}>
                                    {formatWorkstreamLetter(index)}. {wsProgress.workstreamName}
                                </div>
                                <StatusTooltipContent counts={wsProgress.statusCounts} />
                            </TooltipContent>
                        </Tooltip>
                    );
                }) ?? <div className={"h-full w-full rounded-full bg-input"}></div>}
            </div>
            {progress ? (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className={cn(
                                "mt-[1px] w-fit text-xs font-medium text-muted-foreground",
                                status === "delayed" && "text-destructive"
                            )}
                        >
                            {Math.round((progress.taskCompletion ?? 0) * 100)}% complete
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={"bottom"} className={"max-w-[280px] p-0"}>
                        <StatusTooltipContent counts={progress.statusCounts} />
                    </TooltipContent>
                </Tooltip>
            ) : (
                <div className={"mt-[1px] text-xs font-medium text-muted-foreground"}>No tasks yet</div>
            )}
        </div>
    );
}

function ReadinessSummary() {
    const query = useProjectsOrgReadinessQuery();
    if (!query.data) {
        return null;
    }

    const ready = query.data.usersReady && query.data.backgroundCheckReady;
    if (ready) {
        return null;
    }

    return (
        <div
            className={
                "flex flex-col gap-1 rounded-md border border-yellow-600/70 bg-yellow-600/5 p-4 dark:border-yellow-500/40 dark:bg-yellow-500/5"
            }
        >
            <div
                className={
                    "mb-2 flex flex-row items-center text-sm font-medium text-yellow-600/70 dark:border-yellow-500/40"
                }
            >
                <TriangleAlertIcon className={"me-2 h-5 w-5"} />
                Finish Setup of your Organization
            </div>
            <ReadinessItem done={query.data.backgroundCheckReady}>Company Research Completed</ReadinessItem>
            <ReadinessItem done={query.data.usersReady}>Users Added</ReadinessItem>
        </div>
    );
}

function ReadinessItem(props: React.PropsWithChildren<{ done: boolean }>) {
    return (
        <div className={"flex flex-row items-center gap-3"}>
            {props.done ? (
                <CircleCheckIcon className={"h-5 w-5 opacity-65"} />
            ) : (
                <CircleDashedIcon className={"h-5 w-5"} />
            )}
            <div className={cn(props.done && "opacity-65")}>{props.children}</div>
        </div>
    );
}
